import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef } from 'react';

import { REFS_MAP } from '$src/constants';
import { useThemeContext } from '$src/context/ThemeContext';
import { AnalyticsEventName } from '$src/hooks/client/useTrackAnalytics';
import { useTrackTransition } from '$src/hooks/client/useTrackTransition';
import { getThemeColors } from '$src/utils/themeColors';

type MissingCartDataProps = {
  redirectUrl?: string;
  isOpen: boolean;
};

const MissingCartData = ({ redirectUrl, isOpen }: MissingCartDataProps) => {
  const { themeName } = useThemeContext();
  const colors = getThemeColors(themeName);

  const { t } = useTranslation();
  const cancelRef = useRef(null);

  const redirectToMerchant = useCallback(() => {
    if (redirectUrl) {
      location.href = redirectUrl;
    } else {
      // No redirect URL available, go back in history
      history.back();
    }
  }, [redirectUrl]);

  useTrackTransition({
    value: isOpen,
    trueEventName: AnalyticsEventName.MISSING_CART_DATA_OPENED,
    skipInitial: true,
  });

  return (
    <AlertDialog
      portalProps={{
        containerRef: REFS_MAP.get('portal') as any,
      }}
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef as any}
      onClose={redirectToMerchant}
      isOpen={Boolean(isOpen)}
      isCentered
    >
      <AlertDialogOverlay />

      <AlertDialogContent bg={colors.cardBg} mx={4}>
        <Box display="flex" justifyContent="center" p="4">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="62px"
            height="62px"
            borderRadius="full"
            bg="#FDE8E8"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 18.99C1.55 19.33 1.64 19.67 1.82 19.97C1.99 20.27 2.25 20.52 2.55 20.69C2.86 20.87 3.21 20.96 3.56 20.96H20.44C20.79 20.96 21.14 20.87 21.45 20.69C21.75 20.52 22.01 20.27 22.18 19.97C22.36 19.67 22.45 19.33 22.45 18.99C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.28 3.32 12.98 3.15C12.68 2.98 12.34 2.89 12 2.89C11.66 2.89 11.32 2.98 11.02 3.15C10.72 3.32 10.47 3.56 10.29 3.86Z"
                stroke="#D97706"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="#FBBF24"
              />
            </svg>
          </Box>
        </Box>
        <AlertDialogHeader
          fontSize="1rem"
          pt="1"
          pb="1"
          textAlign="center"
          color={colors.text}
          fontWeight="bold"
        >
          {t('errors.missing_cart_data.title')}
        </AlertDialogHeader>
        <AlertDialogBody fontSize="0.81rem" color={colors.textSecondary} textAlign="center" pb={4}>
          {t('errors.missing_cart_data.description')}
        </AlertDialogBody>

        {/* Info box */}
        <Box mx={6} mb={4} p={4} bg="#FEF3C7" borderLeft="4px solid #D97706" borderRadius="md">
          <Text fontWeight="bold" color="#92400E" fontSize="0.875rem" mb={1}>
            {t('errors.missing_cart_data.what_to_do')}
          </Text>
          <Text color="#92400E" fontSize="0.81rem">
            {t('errors.missing_cart_data.instructions')}
          </Text>
        </Box>

        <AlertDialogFooter justifyContent="center" pb={6}>
          <Button
            ref={cancelRef as any}
            onClick={redirectToMerchant}
            colorScheme="blue"
            px={8}
            borderRadius="full"
          >
            {t('errors.missing_cart_data.redirect')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default MissingCartData;
